import React from 'react';
import { Room } from 'matrix-js-sdk';
import { PhoneHQAvatar, StackedAvatars, type AvatarSize } from 'ch3-ui-lib';
import { Users } from 'lucide-react';
import { useAuthenticatedMedia } from '../../hooks/useAuthenticatedMedia';

interface RoomUser {
    displayName: string;
    avatarUrl?: string;
}

interface RoomAvatarProps {
    room: Room | undefined | null;
    roomUsers: RoomUser[];
    size: AvatarSize;
    resolvedAvatarUrl?: string;
    className?: string;
    onClick?: () => void;
}

const RoomAvatarInner: React.FC<{ mxcUrl: string; name: string; size: AvatarSize; className?: string; onClick?: () => void }> = ({
    mxcUrl, name, size, className, onClick,
}) => {
    const { blobUrl, isLoading } = useAuthenticatedMedia(mxcUrl);

    if (isLoading || !blobUrl) {
        return <PhoneHQAvatar size={size} name={name} className={className} onClick={onClick} />;
    }

    return <PhoneHQAvatar size={size} name={name} src={blobUrl} className={className} onClick={onClick} />;
};

// A group without its own picture: one 32px tile, the same footprint as a
// person's avatar, so rows and headers line up whatever the room type.
export const GroupAvatarTile: React.FC<{ className?: string }> = ({ className = '' }) => (
    <span
        data-testid="group-avatar-tile"
        aria-hidden="true"
        className={`inline-flex size-8 shrink-0 items-center justify-center rounded-control bg-brand-tint-subtle text-brand-ink ${className}`}
    >
        <Users size={16} className="!p-0 !text-current" />
    </span>
);

export const RoomAvatar: React.FC<RoomAvatarProps> = ({ room, roomUsers, size, resolvedAvatarUrl, className, onClick }) => {
    const mxcUrl = room?.getMxcAvatarUrl() || '';
    const stackedUsers = roomUsers.slice(0, 2);

    if (mxcUrl) {
        if (resolvedAvatarUrl !== undefined) {
            return <PhoneHQAvatar size={size} name={room?.name || ''} src={resolvedAvatarUrl || undefined} className={className} onClick={onClick} />;
        }

        return <RoomAvatarInner mxcUrl={mxcUrl} name={room?.name || ''} size={size} className={className} onClick={onClick} />;
    }

    if (className || onClick) {
        return (
            <div className={className} onClick={onClick}>
                <StackedAvatars users={stackedUsers} size={size} />
            </div>
        );
    }

    return <StackedAvatars users={stackedUsers} size={size} />;
};
